import { c as _c } from "react/compiler-runtime";
import { type Factiii } from '@shared/all/domains/factiiis';
import { type Post } from '@shared/all/domains/posts';
import PostItem from '../../posts/PostItem';
import { WORKOUT_IMAGE } from './workoutImage';

// Demo fixtures for the activation funnel's factiii lessons. Pure data rendered
// on a throwaway QueryClient so nothing here creates real server records.

const FIXTURE_NOW = new Date();
const fixtureUser = (id: number, name: string, username: string, tag = 'HUMAN') => ({
  id,
  name,
  username,
  avatarKey: null,
  robohash: username,
  tag,
  types: [] as string[]
});
type FixtureUser = ReturnType<typeof fixtureUser>;
const fixturePill = (factiiiId: number, factName: string, user: FixtureUser) => ({
  id: factiiiId * 10,
  factiiiId,
  name: factName,
  requirements: [] as string[],
  count: 1,
  postFactiiis: [{
    id: factiiiId * 100,
    data: null,
    number: null,
    createdAt: FIXTURE_NOW,
    status: 'APPROVED',
    spaceTimes: [],
    uploads: [],
    scrapeId: null,
    rawData: null,
    references: [],
    factiiiReferences: [],
    user: {
      ...user,
      isIncognito: false,
      anonymous: false
    },
    triggeredByPostFactiii: null
  }]
});
const fixturePost = (opts: {
  id: number;
  user: FixtureUser;
  content: string;
  pills?: ReturnType<typeof fixturePill>[];
  image?: string;
}): Post => ({
  id: opts.id,
  slug: `demo-post-${opts.id}`,
  title: null,
  content: opts.content,
  voteCount: 0,
  voted: null,
  viewCount: 0,
  replies_count: 0,
  replies: [],
  createdAt: FIXTURE_NOW,
  edited: false,
  anonymous: false,
  isPrivate: false,
  repost: null,
  topAnswer: null,
  triggeredByFactiii: null,
  pos: 0,
  pinned: false,
  saved: false,
  user: opts.user,
  space: null,
  uploads: opts.image ? [{
    id: opts.id,
    type: 'IMAGE',
    display: {
      url: opts.image,
      width: 640,
      height: 440
    },
    watermarked: false
  }] : [],
  taggedUsers: [],
  postFactiiisCount: opts.pills ?? [],
  awardCounts: []
}) as unknown as Post;
const MARCO = fixtureUser(900001, 'Marco', 'marco');
const DR_LENA = fixtureUser(900002, 'Dr. Lena', 'dr_lena');
const SAM = fixtureUser(900003, 'Sam', 'sam');
const FACTIII_BOT = fixtureUser(900004, 'factiii-bot', 'factiii-bot', 'BOT');
export const SEED_OIL_POSTS: Post[] = [fixturePost({
  id: 910001,
  user: MARCO,
  content: 'are seed oils actually bad for you?',
  pills: [fixturePill(920001, 'seed oil', MARCO)]
}), fixturePost({
  id: 910002,
  user: DR_LENA,
  content: 'most of the risk is processed food, not the oil',
  pills: [fixturePill(920001, 'seed oil', DR_LENA)]
})];
export const BOT_POST = fixturePost({
  id: 910003,
  user: SAM,
  content: 'why is the sky blue?',
  pills: [fixturePill(920002, 'Einstein', SAM)]
});
export const BOT_REPLY = fixturePost({
  id: 910004,
  user: FACTIII_BOT,
  content: 'ah, a fine question! sunlight scatters off the air, and blue scatters the most, so that’s the color that reaches your eyes.'
});
const YOU = fixtureUser(900005, 'you', 'you');
export const WORKOUT_POST = fixturePost({
  id: 910005,
  user: YOU,
  content: 'leg day at the gym',
  pills: [fixturePill(920003, 'Workout', YOU)],
  image: WORKOUT_IMAGE
});
const lessonFactiii = (id: number, name: string, slug: string): Factiii => ({
  id,
  name,
  username: 'factiii',
  description: null,
  slug,
  spaceSlug: null,
  status: 'APPROVED',
  groupId: null,
  avatarKey: null,
  bannerKey: null,
  replyConfig: undefined,
  requirements: [],
  rules: [],
  owner: null,
  factiiiSlug: slug,
  data: null,
  createdAt: FIXTURE_NOW,
  pos: 0
}) as unknown as Factiii;
export const LESSON_FACTIIIS: Factiii[] = [lessonFactiii(920001, 'seed oil', 'seed-oil'), lessonFactiii(920002, 'Einstein', 'einstein'), lessonFactiii(920003, 'Workout', 'workout')];

// display only, so post actions can't fire mutations
export const LessonPost = t0 => {
  const $ = _c(2);
  const {
    post
  } = t0;
  let t1;
  if ($[0] !== post) {
    t1 = <div className="pointer-events-none"><PostItem post={post} isFullLength={false} isInView={false} disableLink={true} disableTracking={true} /></div>;
    $[0] = post;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};